import { Button } from '@components/Button';
import { ArticleCard } from '@components/Card/';
import Block from '@components/Layout/Block';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { getBlogPostsByTag } from '@data/blog';
import { convertTagNameToSlug, getHostWithProtocol } from '@util/blog';
import { getCorrectHeader, getCurrentLocale } from '@util/locale';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import CardGridWithMobileCarousel from '..';
import {
  CarouselCtaWrapper,
  CarouselHeaderWrapper,
  CarouselHeadlineAlt,
  NoResultCard,
} from '../style';

export interface ShopsFeaturedArticlesProps {
  blogHeader: string;
  viewAllArticlesButtonText?: string;
  contentfulTagIds?: string;
  pageData?: any;
  blogHomepageCheck?: boolean;
  shopname?: string;
  urlPrepend?: string;
}

// **Same as BlogHomepageFeaturedArticles but with different styled header and a CTA
const ShopsFeaturedArticles: FC<ShopsFeaturedArticlesProps> = observer(
  ({
    blogHeader,
    viewAllArticlesButtonText,
    contentfulTagIds,
    pageData,
    blogHomepageCheck,
    shopname,
  }) => {
    const t = useTranslations();
    const locale = getCurrentLocale();
    const { shopStore } = useStores();
    const { shopPathPrefix } = shopStore;

    const [cardArr, setCardArr] = useState<any[]>([]);
    const [isDefaultBlogs, setIsDefaultBlogs] = useState<boolean>(true);
    const fields = pageData?.fields;

    const mapBlogPosts = (items: any[], useDefaultBlogs?: boolean) => {
      const hostWithProtocolAndShopname = getHostWithProtocol(shopname, useDefaultBlogs);

      return items.map((item: any) => ({
        key: item.sys.id,
        img: item?.fields?.img?.fields?.file?.url,
        alt_text: item.fields?.img?.fields?.description ? item.fields.img.fields.description : '',
        headline: item.fields?.title,
        date: item.sys.createdAt,
        bodyText: documentToPlainTextString(item.fields?.bodyText),
        slug: item.fields.slug,
        urlPrepend: shopname && !useDefaultBlogs ? hostWithProtocolAndShopname : '',
        // urlPrepend:
        //   contentfulTagIds === 'americas' || useDefaultBlogs
        //     ? `${window.location.protocol}//${extractDomain(window.location.host)}`
        //     : shopPathPrefix,
        tags: item.fields.blogTags
          ? item.fields.blogTags?.map((tag: any) => ({
              name: t(tag),
              slug: convertTagNameToSlug(tag),
            }))
          : [],
      }));
    };

    const fetchAndSetBlogArr = async () => {
      try {
        if (!contentfulTagIds) {
          console.warn('No contentfulTagIds provided for blog posts');
          return;
        }
        const blogPostsData = await getBlogPostsByTag(contentfulTagIds, 3, locale, false);
        if (blogPostsData.items && blogPostsData.items.length > 0) {
          setIsDefaultBlogs(false);
          setCardArr(mapBlogPosts(blogPostsData.items, false));
        } else {
          const defaultBlogPostsData = await getBlogPostsByTag('americas', 3, locale, false);
          setCardArr(mapBlogPosts(defaultBlogPostsData.items, true));
        }
      } catch (e) {
        console.log(e);
      }
    };

    useEffect(() => {
      if (fields?.featuredArticles?.fields?.articles?.length > 0) {
        setIsDefaultBlogs(false);
        setCardArr(mapBlogPosts(fields?.featuredArticles?.fields?.articles, false));
      } else {
        fetchAndSetBlogArr();
      }
    }, [contentfulTagIds]);

    const cardElements =
      cardArr &&
      cardArr?.length > 0 &&
      cardArr?.map((card: any) => {
        return (
          <ArticleCard
            key={card.key}
            img={card.img}
            alt_text={card.alt_text}
            headline={card.headline}
            date={card.date}
            bodyText={card.bodyText}
            slug={card.slug}
            urlPrepend={card.urlPrepend}
            tags={card.tags}
            backgroundColor={false}
            locale={locale}
          />
        );
      });

    if (cardArr && cardArr?.length > 0) {
      const hostWithProtocol = getHostWithProtocol();
      const header = getCorrectHeader({ headerData: blogHeader }, locale);
      const ctaText =
        viewAllArticlesButtonText &&
        getCorrectHeader({ headerData: viewAllArticlesButtonText }, locale);

      // const ctaUrl =
      //   contentfulTagIds === 'americas' || isDefaultBlogs
      //     ? `/blog?type=buyers-and-sellers`
      //     : `${shopPathPrefix}/blog`;
      const ctaUrl =
        contentfulTagIds === 'americas' || isDefaultBlogs
          ? `${hostWithProtocol}/blog?type=buyers-and-sellers`
          : `/blog`;
      return (
        <Block>
          <CarouselHeaderWrapper>
            <CarouselHeadlineAlt data-testid="featured-articles-header">
              {header ?? t('E&V Blog')}
            </CarouselHeadlineAlt>
          </CarouselHeaderWrapper>
          {cardElements && cardElements.length > 0 ? (
            <CardGridWithMobileCarousel
              dataTestId="featured-articles-carousel"
              cardArr={cardElements}
              cardType="article"
            />
          ) : (
            <NoResultCard>{t('No Articles Found')}</NoResultCard>
          )}
          <CarouselCtaWrapper>
            <Button
              url={ctaUrl}
              label={ctaText ?? t('View All Articles')}
              variant="secondary"
              arrow={false}
            />
          </CarouselCtaWrapper>
        </Block>
      );
    }
  },
);

export default ShopsFeaturedArticles;
